import styled, { keyframes } from "styled-components";

const fadeSlideIn = keyframes`
  from { opacity: 0; transform: translateY(6px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const rowExpand = keyframes`
  from { opacity: 0; transform: translateY(-3px); }
  to   { opacity: 1; transform: translateY(0); }
`;

export const MainDiv = styled.div`
  display: flex;
  flex-direction: row;
  flex-grow: 1;
  background: #F0F2FA;
  overflow: hidden;
`;

export const ContentWrapDiv = styled.div`
  display: flex;
  flex-direction: column;
  height: 100vh;
  padding: 28px 32px 24px 32px;
  flex: 1;
  min-width: 0;
  overflow: hidden;
  font-family: 'DM Sans', 'Segoe UI', sans-serif;
  box-sizing: border-box;
  border-top: 5px solid #6366F1;
`;

export const TopContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 20px;
  flex-shrink: 0;
  gap: 12px;
  flex-wrap: wrap;

  .left { display: flex; flex-direction: column; gap: 3px; }

  .page-title {
    font-weight: 900;
    font-size: 30px;
    line-height: 1.1;
    color: #0F172A;
    letter-spacing: -0.6px;
  }

  .date-label {
    font-size: 13px;
    color: #64748B;
    font-weight: 700;
  }

  .right { display: flex; align-items: center; gap: 8px; padding-top: 4px; }
`;

export const RangeTabsWrapper = styled.div`
  display: flex;
  background: #E2E5F5;
  border-radius: 12px;
  padding: 4px;
  gap: 2px;
`;

export const RangeTab = styled.button`
  padding: 6px 13px;
  border-radius: 9px;
  border: none;
  background: ${({ active }) => active
    ? "linear-gradient(135deg, #4F46E5 0%, #7C3AED 100%)"
    : "transparent"};
  color: ${({ active }) => active ? "#fff" : "#64748B"};
  font-weight: ${({ active }) => active ? 700 : 500};
  font-size: 13px;
  cursor: pointer;
  transition: all 0.18s ease;
  white-space: nowrap;
  box-shadow: ${({ active }) => active ? "0 2px 10px rgba(79,70,229,0.35)" : "none"};

  &:hover {
    background: ${({ active }) => active
      ? "linear-gradient(135deg, #4338CA 0%, #6D28D9 100%)"
      : "#D4D8EE"};
    color: ${({ active }) => active ? "#fff" : "#374151"};
  }
`;

export const FilterRow = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: nowrap;
  align-items: center;
  margin-bottom: 16px;
  flex-shrink: 0;
  white-space: nowrap;

  overflow-x: auto;
  overflow-y: hidden;

  scrollbar-width: thin;
  scrollbar-color: #d1d5db transparent;

  &::-webkit-scrollbar {
    height: 4px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: #d1d5db;
    border-radius: 10px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #bfc5cd;
  }

  .date-sep {
    font-size: 14px;
    color: #94A3B8;
    font-weight: 600;
  }
`;

export const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 10px;
  margin-bottom: 16px;
  flex-shrink: 0;

  @media (max-width: 1000px) { grid-template-columns: repeat(3, 1fr); }
  @media (max-width: 640px)  { grid-template-columns: repeat(2, 1fr); }
`;

export const SummaryCard = styled.div`
  background: #fff;
  border-radius: 14px;
  padding: 16px 18px 14px;
  position: relative;
  overflow: hidden;
  box-shadow: 0 1px 2px rgba(0,0,0,0.04), 0 4px 16px rgba(0,0,0,0.04);
  border: 1px solid ${({ color }) => color}22;
  animation: ${fadeSlideIn} 0.3s ease both;
  animation-delay: ${({ idx }) => (idx || 0) * 0.06}s;
  transition: transform 0.18s ease, box-shadow 0.18s ease;

  &::before {
    content: "";
    position: absolute;
    top: 0; left: 0; right: 0;
    height: 3px;
    background: ${({ color }) => color};
    border-radius: 14px 14px 0 0;
  }

  &::after {
    content: "";
    position: absolute;
    bottom: -12px; right: -12px;
    width: 72px; height: 72px;
    border-radius: 50%;
    background: ${({ color }) => color}10;
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 24px ${({ color }) => color}1F;
  }
`;

export const SummaryValue = styled.div`
  font-size: 32px;
  font-weight: 800;
  color: ${({ color }) => color};
  line-height: 1;
  letter-spacing: -1.2px;
  margin-bottom: 6px;
  font-variant-numeric: tabular-nums;
  position: relative;
  z-index: 1;
`;

export const SummaryLabel = styled.div`
  font-size: 9.5px;
  color: #94A3B8;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1px;
  position: relative;
  z-index: 1;
`;

export const TableScrollArea = styled.div`
  border-radius: 16px;
  box-shadow: 0 1px 4px rgba(0,0,0,0.06),
              0 6px 28px rgba(0,0,0,0.07);
  background: #fff;
  overflow-y: auto;
  overflow-x: hidden;
  max-height: calc(100vh - 310px);

  &::-webkit-scrollbar { width: 4px; height: 4px; }
  &::-webkit-scrollbar-thumb { background: #C7D2FE; border-radius: 10px; }
`;

export const MainTable = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  font-size: 13px;
  table-layout: auto;

  thead {
    position: sticky;
    top: 0;
    z-index: 10;
  }

  th, td {
    padding: 0 8px;
    // height: 38px;
    text-align: center;
    vertical-align: middle;
  }

  td {
    height: 48px;
    color: #334155;
    border-bottom: 1px solid #F1F4FB;
  }

  tbody tr:last-child td { border-bottom: 0 !important; }
`;

export const THMeta = styled.th`
  background: #F8F9FF;
  padding: 0 10px;
  font-weight: 700;
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.9px;
  color: #64748B;
  text-align: ${({ left }) => left ? "left" : "center"};
  white-space: nowrap;
  vertical-align: middle;
  line-height: 1.35;
  border-bottom: 1.5px solid #E4E9F8;
`;

export const THGroup = styled.th`
  height: 26px;
  padding: 0 10px;
  font-weight: 800;
  font-size: 9.5px;
  text-transform: uppercase;
  letter-spacing: 1px;
  text-align: center;
  white-space: nowrap;
  color: ${({ groupColor }) => groupColor};
  background: ${({ groupColor }) =>
    groupColor === "#F59E0B" ? "#FFFBEB" :
    groupColor === "#8B5CF6" ? "#F5F3FF" : "#F8F9FF"};
  border-bottom: 1.5px solid ${({ groupColor }) => groupColor}50;
  border-left: 1.5px solid ${({ groupColor }) => groupColor}30;
  &:first-of-type { border-left: none; }
`;

export const THSub = styled.th`
  height: 34px;
  padding: 0 8px;
  font-weight: 700;
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  text-align: center;
  border-bottom: 1.5px solid #E4E9F8;
  border-left: 1px solid #EEF0FA;
  white-space: nowrap;
  line-height: 1.2;
  vertical-align: middle;
  background: ${({ bg }) => bg || "#F8F9FF"};
  color: ${({ color }) => color || "#64748B"};
`;

export const RecruiterRow = styled.tr`
  cursor: pointer;
  td { background: #F8F9FF; border-bottom: 1px solid #EAEEF8; }
  &:hover td { background: #e9ebfdff; }
`;

export const DemandRow = styled.tr`
  td { background: #fff; }
  &:hover td { background: #faede7ff; }
  animation: ${rowExpand} 0.16s ease both;
`;

export const AssessYesBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 3px 10px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 700;
  white-space: nowrap;
  background: #D1FAE5;
  color: #065F46;
  border: 1px solid #6EE7B7;
`;

export const AssessNoBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 3px 10px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 700;
  white-space: nowrap;
  background: #F1F5F9;
  color: #94A3B8;
  border: 1px solid #CBD5E1;
`;

export const SubtotalRow = styled.tr`
  td {
    background: #EDEEFF !important;
    font-weight: 700 !important;
    color: #4338CA !important;
    border-top: 1.5px solid #C7D2FE;
    border-bottom: 2px solid #C7D2FE !important;
    font-size: 12.5px !important;
    font-variant-numeric: tabular-nums;
  }
  td.subtotal-name {
    text-align: left !important;
    padding-left: 16px !important;
    font-size: 13px !important;
    color: #3730A3 !important;
    white-space: nowrap;
  }
`;

export const GrandTotalRow = styled.tr`
  position: sticky;
  bottom: -1px;
  z-index: 5;

  td {
    height: 52px !important;
    background: linear-gradient(90deg, #1E1B4B 0%, #3730A3 100%) !important;
    font-weight: 800 !important;
    color: #C7D2FE !important;
    font-size: 14px !important;
    border-bottom: none !important;
    font-variant-numeric: tabular-nums;
    &.label { color: #fff !important; font-size: 15px !important; }
  }
`;

export const RecruiterNameCell = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

export const Chevron = styled.span`
  font-size: 7px;
  color: #6366F1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border-radius: 5px;
  background: #EEF2FF;
  transition: transform 0.2s ease;
  transform: ${({ open }) => open ? "rotate(90deg)" : "rotate(0deg)"};
  flex-shrink: 0;
`;

export const RecruiterChip = styled.span`
  display: inline-block;
  padding: 4px 11px;
  border-radius: 7px;
  font-size: 12px;
  font-weight: 700;
  white-space: nowrap;
  background: ${({ bg }) => bg};
  color: ${({ fg }) => fg};
`;

export const FilterRecruiterChip = styled.span`
  display: inline-flex;
  align-items: center;
  height: 24px;
  padding: 0 10px;
  border-radius: 7px;
  font-size: 12px;
  font-weight: 700;
  white-space: nowrap;
  background: ${({ bg }) => bg};
  color: ${({ fg }) => fg};
`;

export const FilterClientChip = styled.span`
  display: inline-flex;
  align-items: center;
  height: 22px;
  padding: 0 9px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 700;
  white-space: nowrap;
  background: ${({ bg }) => bg};
  color: ${({ fg }) => fg};
`;

export const ClientChip = styled.span`
  display: inline-block;
  padding: 3px 9px;
  border-radius: 6px;
  font-size: 10.5px;
  font-weight: 700;
  white-space: nowrap;
`;

export const ColorPill = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 28px;
  padding: 3px 9px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  background: ${({ bg }) => bg};
  color: ${({ fg }) => fg};
`;

export const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 3px 10px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 700;
  white-space: nowrap;

  &.pending { background: #FEF9EC; color: #B45309; border: 1px solid #FDE68A; }
  &.sent    { background: #F0FDF4; color: #166534; border: 1px solid #BBF7D0; }
`;

export const NumSpan = styled.span`
  font-size: 13px;
  font-weight: ${({ zero }) => zero ? 400 : 700};
  color: ${({ zero }) => zero ? "#CBD5E1" : "#0F172A"};
  font-variant-numeric: tabular-nums;
`;

export const RoleCellWrapper = styled.td`
  text-align: left !important;
  padding-left: 16px !important;
  font-size: 12px;
  max-width: 0;
  overflow: hidden;
  position: relative;
  cursor: default;

  span.role-text {
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-weight: 500;
    color: #334155;
    transition: color 0.15s;
    &:hover { color: #4F46E5; }
  }
`;

export const RoleTooltip = styled.div`
  position: fixed;
  z-index: 9999;
  pointer-events: none;
  background: #1E1B4B;
  color: #E0E7FF;
  padding: 7px 13px;
  border-radius: 9px;
  font-size: 12.5px;
  font-weight: 500;
  white-space: nowrap;
  letter-spacing: 0.01em;
  box-shadow: 0 8px 24px rgba(79,70,229,0.25);
  border-left: 3px solid #818CF8;
  opacity: ${({ visible }) => visible ? 1 : 0};
  transform: ${({ visible }) => visible ? "translateY(0)" : "translateY(4px)"};
  transition: opacity 0.15s ease, transform 0.15s ease;
`;

export const SpinWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 200px;
`;

export const EmptyWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 0;
  color: #94A3B8;
  font-size: 14px;
  gap: 12px;

  .icon { font-size: 40px; }
`;

export const StyledDatePickerWrap = styled.div`
  .ant-picker {
    height: 42px !important;
    border-radius: 12px !important;
    display: flex;
    align-items: center;
    padding: 0 12px;
    border: 1px solid #D7DDED;
    box-shadow: none;
  }
  .ant-picker:hover { border-color: #A5B4FC; }
  .ant-picker-focused {
    border-color: #818CF8 !important;
    box-shadow: 0 0 0 3px rgba(99,102,241,0.08) !important;
  }
  .ant-picker-input { height: 100%; display: flex; align-items: center; }
  .ant-picker-input > input {
    height: 100%; display: flex; align-items: center;
    font-size: 14px; font-weight: 600; color: #334155;
  }
  .ant-picker-suffix {
    position: static !important; margin: 0 !important;
    padding-left: 8px; display: flex; align-items: center;
    justify-content: center; color: #94A3B8; transform: translateY(0px);
  }
  .ant-picker-clear { display: flex; align-items: center; }
`;

export const StyledSelectWrap = styled.div`
  .ant-select-selector {
    height: 42px !important;
    border-radius: 12px !important;
    border: 1px solid #D7DDED !important;
    display: flex; align-items: center;
    padding: 0 12px !important;
    box-shadow: none !important;
  }
  .ant-select-selection-item,
  .ant-select-selection-placeholder,
  .ant-select-selection-search-input {
    height: 40px !important; line-height: 40px !important;
    display: flex; align-items: center;
    font-size: 14px; font-weight: 500;
  }
  .ant-select-arrow { color: #94A3B8; }
  .ant-select-focused .ant-select-selector {
    border-color: #818CF8 !important;
    box-shadow: 0 0 0 3px rgba(99,102,241,0.08) !important;
  }
  .ant-select-selector:hover { border-color: #A5B4FC !important; }
`;



export const DashboardSwitch = styled.div`
    display: flex;
    align-items: center;
    gap: 14px;
    margin-bottom: 16px;
`;

export const SwitchTab = styled.div`
    padding: 12px 24px;
    border-radius: 14px;
    cursor: pointer;

    font-size: 16px;
    font-weight: 700;
    min-width: 190px;
    text-align: center;

    transition: all 0.25s ease;

    background: ${({ active }) =>
        active ? "#5A4FF3" : "#FFFFFF"};

    color: ${({ active }) =>
        active ? "#FFFFFF" : "#5B6B8A"};

    border: ${({ active }) =>
        active ? "2px solid #5A4FF3" : "2px solid #E5E7EB"};

    box-shadow: ${({ active }) =>
        active
            ? "0 8px 20px rgba(90, 79, 243, 0.25)"
            : "0 2px 8px rgba(0, 0, 0, 0.06)"};

    user-select: none;

    &:hover {
        transform: translateY(-2px);
        box-shadow: ${({ active }) =>
            active
                ? "0 10px 24px rgba(90, 79, 243, 0.3)"
                : "0 6px 16px rgba(0, 0, 0, 0.1)"};
    }

    &:active {
        transform: scale(0.98);
    }
`;


export const DebriefSummaryGrid = styled(SummaryGrid)`
  grid-template-columns: repeat(7, 1fr);

  @media (max-width: 1200px) { grid-template-columns: repeat(4, 1fr); }
  @media (max-width: 800px)  { grid-template-columns: repeat(2, 1fr); }
`;

export const DebriefCardsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 8px;
  margin-bottom: 20px;
`;